import { memo, useMemo, useState } from "react";
import {
  ALL_LAYERS,
  BASE_MAPS,
  LAYER_GROUP_LABEL,
  LENSES,
  type BaseMapId,
  type LayerGroup,
  type LayerId,
   type LensId,
} from "../map/presets";
import { ZONE_METRIC_LABEL, type ZoneMetric } from "../map/layers";

interface Props {
  lens: LensId;
  onLensChange: (l: LensId) => void;
  enabled: Set<LayerId>;
  onToggleLayer: (id: LayerId) => void;
  onClearAllLayers?: () => void;
  baseMap: BaseMapId;
  onBaseMapChange: (b: BaseMapId) => void;
  /** Active subdistrict-zone colour metric. */
  zoneMetric: ZoneMetric;
  onZoneMetricChange: (m: ZoneMetric) => void;
}

// Top-level hierarchy the user reads:
//   1. Base map (mutually exclusive ground style)
//   2. Lens (preset bundle of layers)
//   3. Layer groups (additive toggles)
// AQ lives inside "campus" — both station dots and the IQAir heatmap.
const GROUP_ORDER: LayerGroup[] = ["campus", "neighborhood", "mobility", "incidents", "utility", "environment", "imagery", "xray", "district"];

// Default explainer when nothing is hovered — tells the user the line is live.
const LAYERS_HINT = "Hover a layer to see what it shows. Toggle any combination — overlays stack on the base map.";

// The imagery group mixes orbital products that look alike but mean very
// different things. A persistent one-liner decodes the category up front.
const IMAGERY_HINT =
  "Satellite layers swap the map for orbital imagery. Some auto-zoom out to regional view (the campus is too small for their resolution). NDVI = vegetation health, LST = surface heat, NO₂/aerosol = air pollutants, true-color = what you'd see from space.";

// memo: App re-renders per RAF frame during camera moves; palette props are
// referentially stable (useCallback handlers + Set identity), so skip those.
function LayerPaletteImpl({
  lens,
  onLensChange,
  enabled,
  onToggleLayer,
  onClearAllLayers,
  baseMap,
  onBaseMapChange,
  zoneMetric,
  onZoneMetricChange,
}: Props) {
  // group → list of layers (preserve declaration order within each group)
  const grouped = useMemo(() => {
    const m = new Map<LayerGroup, typeof ALL_LAYERS>();
    for (const g of GROUP_ORDER) m.set(g, []);
    for (const l of ALL_LAYERS) m.get(l.group)?.push(l);
    return m;
  }, []);

  const [collapsed, setCollapsed] = useState<Record<LayerGroup, boolean>>(() => ({
    campus: false,
    neighborhood: true,
    mobility: false,
    incidents: true,
    utility: true,
    environment: false,  // open — canopy, AQ, AlphaEarth
    imagery: false,      // open — AlphaEarth land cover / change / greenness
    xray: false,
    district: true,      // collapsed by default — boundary + POI toggles
  }));
  const toggleGroup = (g: LayerGroup) => setCollapsed((c) => ({ ...c, [g]: !c[g] }));

  const enabledByGroup = (g: LayerGroup) =>
    (grouped.get(g) ?? []).filter((l) => enabled.has(l.id)).length;

  // Layer the cursor/focus is on — drives the inline explainer line below the
  // groups. null → show the default hint. Mirrors the Base map / Lens pattern,
  // but hover-driven since layers are many and additive.
  const [hovered, setHovered] = useState<LayerId | null>(null);
  const hoveredDescribe = hovered
    ? ALL_LAYERS.find((l) => l.id === hovered)?.describe ?? LAYERS_HINT
    : LAYERS_HINT;

  return (
    <div className="col">
      <div>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Base map</div>
        <div className="basemap-picker" role="radiogroup" aria-label="Base map style">
          {BASE_MAPS.map((bm) => (
            <button
              key={bm.id}
              type="button"
              role="radio"
              aria-checked={baseMap === bm.id}
              onClick={() => onBaseMapChange(bm.id)}
              className={`basemap-chip ${baseMap === bm.id ? "active" : ""}`}
              title={bm.describe}
              aria-label={bm.describe}
            >
              <span className="basemap-swatch" style={{ background: bm.swatch }} />
              <span className="basemap-label">{bm.label}</span>
            </button>
          ))}
        </div>
        <div className="lens-explainer caption">
          {BASE_MAPS.find((b) => b.id === baseMap)?.describe}
        </div>
      </div>

      <hr className="divider" />

      <div>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Lens</div>
        <div className="lens">
          {LENSES.map((l) => (
            <button
              key={l.id}
              onClick={() => onLensChange(l.id)}
              aria-pressed={lens === l.id}
              className={lens === l.id ? "active" : ""}
              title={l.describe}
              aria-label={`${l.label} — ${l.describe}`}
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="lens-explainer caption">
          {LENSES.find((l) => l.id === lens)?.describe}
        </div>
      </div>

      <hr className="divider" />

      <div>
        <div className="eyebrow" style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Layers</span>
          <button
            type="button"
            onClick={onClearAllLayers ?? (() => {
              Array.from(enabled).forEach((id) => onToggleLayer(id));
            })}
            style={{ fontSize: "0.7rem", padding: "2px 6px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "4px", color: "var(--text-2)", cursor: "pointer" }}
            aria-label="Clear all overlays"
            title="Turn off all data layers"
          >
            Clear All
          </button>
        </div>
        <div className="layer-groups">
          {GROUP_ORDER.map((g) => {
            const list = grouped.get(g) ?? [];
            if (list.length === 0) return null;
            const on = enabledByGroup(g);
            const isCollapsed = collapsed[g];
            return (
              <section key={g} className={`layer-group ${isCollapsed ? "is-collapsed" : ""}`}>
                <button
                  type="button"
                  className="layer-group-head"
                  onClick={() => toggleGroup(g)}
                  aria-expanded={!isCollapsed}
                  aria-label={`${LAYER_GROUP_LABEL[g]} layers (${on} of ${list.length} on)`}
                >
                  <span className="layer-group-name mono">{LAYER_GROUP_LABEL[g]}</span>
                  <span className="layer-group-meta mono">{on}/{list.length}</span>
                  <span className="layer-group-chevron mono">{isCollapsed ? "▸" : "▾"}</span>
                </button>
                {!isCollapsed && (
                  <div className="layer-toggles">
                    {g === "imagery" && (
                      <div className="lens-explainer caption">{IMAGERY_HINT}</div>
                    )}
                    {list.map((l) => {
                      const isOn = enabled.has(l.id);
                      return (
                        <div
                          key={l.id}
                          className={`layer-toggle ${isOn ? "on" : "off"}`}
                          role="checkbox"
                          aria-checked={isOn}
                          tabIndex={0}
                          title={l.describe}
                          aria-label={`${l.label} — ${l.describe}`}
                          onClick={() => onToggleLayer(l.id)}
                          onMouseEnter={() => setHovered(l.id)}
                          onMouseLeave={() => setHovered((h) => (h === l.id ? null : h))}
                          onFocus={() => setHovered(l.id)}
                          onBlur={() => setHovered((h) => (h === l.id ? null : h))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onToggleLayer(l.id);
                            }
                          }}
                        >
                          <span className="row">
                            <span className="swatch" style={{ background: l.swatch }} />
                            <span>{l.label}</span>
                            {l.id.startsWith("alphaearth-") && (
                              <span style={{ marginLeft: 4, fontSize: "0.6rem", background: "rgba(139,92,246,0.25)", color: "#c4b5fd", borderRadius: 3, padding: "1px 4px", fontWeight: 700, letterSpacing: "0.05em" }}>AI</span>
                            )}
                            {(l.id.startsWith("satellite-") && l.id !== "satellite-esri") && (
                              <span style={{ marginLeft: 4, fontSize: "0.6rem", color: "var(--text-3)", opacity: 0.7 }} title="Auto-zooms out to regional view">↙ regional</span>
                            )}
                          </span>
                          <span className="mono caption">{isOn ? "on" : "off"}</span>
                        </div>
                      );
                    })}
                    {/* Zone metric picker — appears under the zones toggle when it's on.
                        Lets the user re-bucket the polygon fills by population /
                        business / risk / students without flipping the layer itself. */}
                    {enabled.has("lat-krabang-zones") && (
                      <div
                        className="zone-metric-picker"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <span className="eyebrow" style={{ marginBottom: 4, fontSize: "0.6rem" }}>ZONE METRIC</span>
                        <div className="zone-metric-pills" role="radiogroup" aria-label="Zone metric">
                          {(Object.keys(ZONE_METRIC_LABEL) as ZoneMetric[]).map((m) => (
                            <button
                              key={m}
                              type="button"
                              role="radio"
                              aria-checked={zoneMetric === m}
                              className={`zone-metric-pill ${zoneMetric === m ? "active" : ""}`}
                              title={ZONE_METRIC_LABEL[m].describe}
                              onClick={() => onZoneMetricChange(m)}
                            >
                              {ZONE_METRIC_LABEL[m].label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
        <div className="lens-explainer caption">{hoveredDescribe}</div>
      </div>
    </div>
  );
}

export const LayerPalette = memo(LayerPaletteImpl);
